import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import Select from 'react-select';
import { Link, useSearchParams } from 'react-router-dom';
import { Card, CardBody, Chip, Dialog, DialogHeader, DialogBody, DialogFooter, Button } from '@material-tailwind/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faEye, faDownload, faEnvelope, faLink, faRotate, faSpinner, faCopy, faCheck, faArrowLeft, faFolderOpen
} from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../AuthContext';
import ConfirmModal from '../../common/ConfirmModal';
import { formatMoney } from '../owners/formatMoney';
import { rootUrl } from '../../../config/config';
import {
    describeReportFormat,
    generateSavedReport,
    downloadSavedReport,
    viewSavedReport,
    emailSavedReport,
    createSavedReportShareLink,
    listSavedReports,
    fetchSavedReportEmails,
    fetchSavedReportEmailDraft
} from './savedRemittanceReports';

const PAGE_SIZE = 25;
const EMAIL_PATTERN = /\S+@\S+\.\S+/;

const selectStyles = {
    control: (base) => ({ ...base, backgroundColor: '#f5f5f5', borderColor: '#d1d5db', '&:hover': { borderColor: '#9ca3af' } }),
    option: (base, state) => ({
        ...base,
        backgroundColor: state.isSelected ? '#FF5733' : state.isFocused ? '#f3f4f6' : 'white',
        color: state.isSelected ? 'white' : 'black',
        cursor: 'pointer'
    }),
    menuPortal: (base) => ({ ...base, zIndex: 9999 })
};

const STATUS_OPTIONS = [
    { value: 'generated', label: 'Current' },
    { value: 'superseded', label: 'Outdated / replaced' }
];

const FORMAT_OPTIONS = [
    { value: 'pdf', label: 'PDF' },
    { value: 'csv', label: 'CSV' }
];

const formatDateTime = (value) => (value
    ? new Date(value).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' })
    : '');

const statusChip = (report) => {
    if (report.status === 'generated') {
        return <Chip size="sm" value="Current" className="bg-green-100 text-green-800 normal-case w-fit" />;
    }
    const label = report.superseded_reason === 'regenerated' ? 'Replaced' : 'Outdated';
    return <Chip size="sm" value={label} className="bg-amber-100 text-amber-800 normal-case w-fit" />;
};

const supersededHint = (report) => {
    if (report.status !== 'superseded') return '';
    const when = formatDateTime(report.superseded_at);
    return report.superseded_reason === 'regenerated'
        ? `A newer copy was saved ${when}${report.superseded_by ? ` by ${report.superseded_by}` : ''}.`
        : `The week's figures changed ${when}. Regenerate to send current numbers.`;
};

const RemittanceReports = () => {
    const { token } = useAuth();
    const [searchParams, setSearchParams] = useSearchParams();

    const [owners, setOwners] = useState([]);
    const [weeks, setWeeks] = useState([]);
    const [filters, setFilters] = useState({
        owner_code: searchParams.get('owner') || '',
        week_key: searchParams.get('week') || '',
        status: searchParams.get('status') || '',
        format: ''
    });
    const [offset, setOffset] = useState(0);
    const [reports, setReports] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [listError, setListError] = useState('');
    const [rowBusy, setRowBusy] = useState({});
    const [rowError, setRowError] = useState({});

    const [emailTarget, setEmailTarget] = useState(null);
    const [emailForm, setEmailForm] = useState({ recipient: '', subject: '', message: '' });
    const [emailHistory, setEmailHistory] = useState([]);
    const [emailSending, setEmailSending] = useState(false);
    const [emailError, setEmailError] = useState('');
    const [emailSuccess, setEmailSuccess] = useState('');
    const [emailDraft, setEmailDraft] = useState(null);
    const [emailDraftLoading, setEmailDraftLoading] = useState(false);
    const emailTargetIdRef = useRef(null);

    const [shareTarget, setShareTarget] = useState(null);
    const [shareLink, setShareLink] = useState(null);
    const [shareError, setShareError] = useState('');
    const [copied, setCopied] = useState(false);

    const [regenerateTarget, setRegenerateTarget] = useState(null);
    const [regenerating, setRegenerating] = useState(false);
    const [regenerateError, setRegenerateError] = useState('');

    useEffect(() => {
        const headers = { Authorization: `Bearer ${token}` };
        axios.get(`${rootUrl}/owners`, { headers })
            .then((response) => setOwners(response.data || []))
            .catch(() => setOwners([]));
        axios.get(`${rootUrl}/system/weeks`, { headers })
            .then((response) => setWeeks(response.data?.data || []))
            .catch(() => setWeeks([]));
    }, [token]);

    const loadReports = useCallback(async () => {
        setLoading(true);
        setListError('');
        try {
            const result = await listSavedReports({ token, filters: { ...filters, limit: PAGE_SIZE, offset } });
            setReports(result.data || []);
            setTotal(result.total || 0);
        } catch (error) {
            setListError(error.message);
        } finally {
            setLoading(false);
        }
    }, [token, filters, offset]);

    useEffect(() => { loadReports(); }, [loadReports]);

    const updateFilter = (key, value) => {
        const next = { ...filters, [key]: value || '' };
        setFilters(next);
        setOffset(0);
        const params = {};
        if (next.owner_code) params.owner = next.owner_code;
        if (next.week_key) params.week = next.week_key;
        if (next.status) params.status = next.status;
        setSearchParams(params, { replace: true });
    };

    const ownerOptions = useMemo(() => owners.map((owner) => ({ value: owner.code, label: `${owner.name} (${owner.code})` })), [owners]);
    const weekOptions = useMemo(() => weeks.map((week) => ({ value: week.week, label: week.week })), [weeks]);
    const ownerEmail = (code) => owners.find((owner) => owner.code === code)?.email || '';

    const runRowAction = async (report, action, work) => {
        setRowBusy((prev) => ({ ...prev, [report.id]: action }));
        setRowError((prev) => ({ ...prev, [report.id]: '' }));
        try {
            await work();
        } catch (error) {
            setRowError((prev) => ({ ...prev, [report.id]: error.message }));
        } finally {
            setRowBusy((prev) => ({ ...prev, [report.id]: null }));
        }
    };

    const openEmail = async (report) => {
        emailTargetIdRef.current = report.id;
        setEmailTarget(report);
        setEmailForm({ recipient: ownerEmail(report.owner_code), subject: '', message: '' });
        setEmailDraft(null);
        setEmailError('');
        setEmailSuccess('');
        setEmailHistory([]);
        setEmailDraftLoading(true);

        const [draftResult, historyResult] = await Promise.allSettled([
            fetchSavedReportEmailDraft({ token, reportId: report.id }),
            fetchSavedReportEmails({ token, reportId: report.id })
        ]);
        // The dialog may have been closed or switched to another report meanwhile.
        if (emailTargetIdRef.current !== report.id) return;

        setEmailDraftLoading(false);
        setEmailHistory(historyResult.status === 'fulfilled' ? historyResult.value : []);
        if (draftResult.status === 'fulfilled' && draftResult.value) {
            const draft = draftResult.value;
            setEmailDraft(draft);
            setEmailForm((prev) => ({
                recipient: prev.recipient || draft.recipient_email || '',
                subject: draft.subject || '',
                message: draft.message || ''
            }));
        } else {
            setEmailError('The suggested subject and message could not be loaded. Type your own, or leave them blank to use the default.');
        }
    };

    const closeEmail = () => {
        emailTargetIdRef.current = null;
        setEmailTarget(null);
    };

    const resetEmailDraft = () => {
        if (!emailDraft) return;
        setEmailForm((prev) => ({ ...prev, subject: emailDraft.subject, message: emailDraft.message }));
    };

    const sendEmail = async () => {
        const recipient = emailForm.recipient.trim();
        if (recipient && !EMAIL_PATTERN.test(recipient)) {
            setEmailError('Enter a valid email address, for example owner@example.com.');
            return;
        }
        setEmailSending(true);
        setEmailError('');
        try {
            const result = await emailSavedReport({
                token,
                reportId: emailTarget.id,
                recipientEmail: recipient,
                subject: emailForm.subject.trim(),
                message: emailForm.message.trim()
            });
            setEmailSuccess(result.message || 'Report emailed.');
            setEmailHistory(await fetchSavedReportEmails({ token, reportId: emailTarget.id }).catch(() => emailHistory));
            loadReports();
        } catch (error) {
            setEmailError(error.message);
        } finally {
            setEmailSending(false);
        }
    };

    const openShare = async (report) => {
        setShareTarget(report);
        setShareLink(null);
        setShareError('');
        setCopied(false);
        try {
            setShareLink(await createSavedReportShareLink({ token, reportId: report.id }));
        } catch (error) {
            setShareError(error.message);
        }
    };

    const copyShareLink = async () => {
        if (!shareLink?.url) return;
        try {
            await navigator.clipboard.writeText(shareLink.url);
            setCopied(true);
        } catch (error) {
            setShareError('Copy failed. Select the link and copy it manually.');
        }
    };

    const confirmRegenerate = async () => {
        if (!regenerateTarget) return;
        setRegenerating(true);
        setRegenerateError('');
        try {
            await generateSavedReport({
                token,
                ownerCode: regenerateTarget.owner_code,
                weekKey: regenerateTarget.week_key,
                format: regenerateTarget.format,
                layout: regenerateTarget.layout || undefined
            });
            setRegenerateTarget(null);
            loadReports();
        } catch (error) {
            setRegenerateError(error.message);
        } finally {
            setRegenerating(false);
        }
    };

    const actionButtons = (report) => {
        const busy = rowBusy[report.id];
        const button = (key, icon, label, onClick, extraClass = '') => (
            <button
                key={key}
                type="button"
                onClick={onClick}
                disabled={!!busy}
                title={label}
                className={`flex items-center gap-1 rounded-md border border-gray-300 px-2 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 ${extraClass}`}
            >
                <FontAwesomeIcon icon={busy === key ? faSpinner : icon} spin={busy === key} />
                <span className="hidden xl:inline">{label}</span>
            </button>
        );
        return (
            <div className="flex flex-wrap gap-1">
                {report.format === 'pdf' && button('view', faEye, 'View', () => runRowAction(report, 'view', () => viewSavedReport({ token, report })))}
                {button('download', faDownload, 'Download', () => runRowAction(report, 'download', () => downloadSavedReport({ token, report })))}
                {button('email', faEnvelope, 'Email', () => openEmail(report))}
                {button('share', faLink, 'Share', () => openShare(report))}
                {button('regenerate', faRotate, 'Regenerate', () => { setRegenerateError(''); setRegenerateTarget(report); },
                    report.status === 'superseded' && report.superseded_reason === 'recalculated' ? 'border-[#FF5733] text-[#FF5733]' : '')}
            </div>
        );
    };

    const emailedLabel = (report) => (report.email_count > 0
        ? `${report.email_count}× · last ${formatDateTime(report.last_emailed_at)}`
        : 'Not emailed');

    const pageEnd = Math.min(offset + reports.length, total);

    return (
        <div className="min-h-screen bg-gradient-to-br from-[#fff8f5] to-[#EDE4DA] p-3 sm:p-6 lg:p-8 text-sm">
            <div className="mx-auto w-full max-w-7xl">
                <div className="text-center mb-6">
                    <h1 className="text-3xl sm:text-4xl font-bold text-[#2a2e30] mb-2">Saved Remittance Reports</h1>
                    <div className="w-24 h-1 bg-[#FF5733] mx-auto rounded-full mb-4"></div>
                    <p className="text-sm text-[#2a2e30] opacity-80">
                        Every exported report is kept in Google Drive. Open, email or share a saved copy without recalculating the week.
                    </p>
                    <div className="mt-4 flex justify-center">
                        <Link
                            to="/remittance/owner-report"
                            className="inline-flex items-center gap-2 rounded-full border border-[#E7D6CA] bg-white px-4 py-2 text-xs font-semibold text-[#2a2e30] shadow-sm hover:border-[#FF5733] hover:text-[#FF5733]"
                        >
                            <FontAwesomeIcon icon={faArrowLeft} />
                            Back to Process Remittance
                        </Link>
                    </div>
                </div>

                <Card className="mb-6 rounded-2xl shadow-xl">
                    <CardBody className="grid grid-cols-1 gap-4 md:grid-cols-4">
                        <div>
                            <label htmlFor="saved-reports-owner" className="mb-2 block text-sm font-semibold text-gray-700">Owner</label>
                            <Select
                                inputId="saved-reports-owner"
                                options={ownerOptions}
                                value={ownerOptions.find((option) => option.value === filters.owner_code) || (filters.owner_code ? { value: filters.owner_code, label: filters.owner_code } : null)}
                                onChange={(option) => updateFilter('owner_code', option?.value)}
                                isClearable
                                placeholder="All owners"
                                styles={selectStyles}
                                menuPortalTarget={document.body}
                            />
                        </div>
                        <div>
                            <label htmlFor="saved-reports-week" className="mb-2 block text-sm font-semibold text-gray-700">Week</label>
                            <Select
                                inputId="saved-reports-week"
                                options={weekOptions}
                                value={weekOptions.find((option) => option.value === filters.week_key) || (filters.week_key ? { value: filters.week_key, label: filters.week_key } : null)}
                                onChange={(option) => updateFilter('week_key', option?.value)}
                                isClearable
                                placeholder="All weeks"
                                styles={selectStyles}
                                menuPortalTarget={document.body}
                            />
                        </div>
                        <div>
                            <label htmlFor="saved-reports-status" className="mb-2 block text-sm font-semibold text-gray-700">Status</label>
                            <Select
                                inputId="saved-reports-status"
                                options={STATUS_OPTIONS}
                                value={STATUS_OPTIONS.find((option) => option.value === filters.status) || null}
                                onChange={(option) => updateFilter('status', option?.value)}
                                isClearable
                                placeholder="Any status"
                                styles={selectStyles}
                                menuPortalTarget={document.body}
                            />
                        </div>
                        <div>
                            <label htmlFor="saved-reports-format" className="mb-2 block text-sm font-semibold text-gray-700">Format</label>
                            <Select
                                inputId="saved-reports-format"
                                options={FORMAT_OPTIONS}
                                value={FORMAT_OPTIONS.find((option) => option.value === filters.format) || null}
                                onChange={(option) => updateFilter('format', option?.value)}
                                isClearable
                                placeholder="PDF and CSV"
                                styles={selectStyles}
                                menuPortalTarget={document.body}
                            />
                        </div>
                    </CardBody>
                </Card>

                <Card className="rounded-2xl shadow-xl">
                    <CardBody className="p-4 md:p-6">
                        {listError && <p className="mb-4 text-sm text-red-500">{listError}</p>}

                        {loading && reports.length === 0 ? (
                            <div className="flex items-center justify-center gap-2 py-12 text-gray-500">
                                <FontAwesomeIcon icon={faSpinner} spin /> Loading saved reports...
                            </div>
                        ) : reports.length === 0 ? (
                            <div className="py-12 text-center text-gray-600">
                                <FontAwesomeIcon icon={faFolderOpen} className="mb-3 text-3xl text-gray-400" />
                                <p className="font-medium">No saved reports match these filters.</p>
                                <p className="mt-1 text-sm">
                                    Use <strong>Export PDF</strong> or <strong>Export CSV</strong> on{' '}
                                    <Link to="/remittance/owner-report" className="text-[#FF5733] hover:underline">Process Remittance</Link>{' '}
                                    to save one.
                                </p>
                            </div>
                        ) : (
                            <>
                                {/* Desktop table */}
                                <div className="hidden overflow-x-auto lg:block">
                                    <table className="w-full text-left text-sm">
                                        <thead>
                                            <tr className="border-b border-gray-200 text-xs uppercase text-gray-500">
                                                <th className="px-3 py-2">Saved</th>
                                                <th className="px-3 py-2">Owner</th>
                                                <th className="px-3 py-2">Week</th>
                                                <th className="px-3 py-2">Format</th>
                                                <th className="px-3 py-2 text-right">Cars</th>
                                                <th className="px-3 py-2 text-right">Owner Payout</th>
                                                <th className="px-3 py-2">Status</th>
                                                <th className="px-3 py-2">Emailed</th>
                                                <th className="px-3 py-2">Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {reports.map((report) => (
                                                <tr key={report.id} className="border-b border-gray-100 align-top hover:bg-gray-50">
                                                    <td className="px-3 py-3 whitespace-nowrap">
                                                        <div>{formatDateTime(report.input_date)}</div>
                                                        <div className="text-xs text-gray-500">{report.input_by}</div>
                                                    </td>
                                                    <td className="px-3 py-3">
                                                        <div className="font-medium">{report.owner_name || report.owner_code}</div>
                                                        <div className="text-xs text-gray-500">{report.owner_code}</div>
                                                    </td>
                                                    <td className="px-3 py-3 whitespace-nowrap">{report.week_key}</td>
                                                    <td className="px-3 py-3 whitespace-nowrap">{describeReportFormat(report)}</td>
                                                    <td className="px-3 py-3 text-right">{report.car_count}</td>
                                                    <td className="px-3 py-3 text-right font-mono whitespace-nowrap">{formatMoney(report.totals_snapshot?.total_owner_payout)}</td>
                                                    <td className="px-3 py-3">
                                                        {statusChip(report)}
                                                        {report.status === 'superseded' && (
                                                            <div className="mt-1 max-w-[14rem] text-xs text-gray-500">{supersededHint(report)}</div>
                                                        )}
                                                    </td>
                                                    <td className="px-3 py-3 text-xs text-gray-600">
                                                        <div>{emailedLabel(report)}</div>
                                                        {report.last_emailed_to && <div className="break-all">{report.last_emailed_to}</div>}
                                                    </td>
                                                    <td className="px-3 py-3">
                                                        {actionButtons(report)}
                                                        {rowError[report.id] && <p className="mt-1 text-xs text-red-500">{rowError[report.id]}</p>}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>

                                {/* Mobile cards */}
                                <div className="space-y-3 lg:hidden">
                                    {reports.map((report) => (
                                        <div key={report.id} className="rounded-xl border border-gray-200 p-4">
                                            <div className="flex items-start justify-between gap-2">
                                                <div>
                                                    <div className="font-semibold">{report.owner_name || report.owner_code}</div>
                                                    <div className="text-xs text-gray-500">{report.owner_code} · Week {report.week_key}</div>
                                                </div>
                                                {statusChip(report)}
                                            </div>
                                            <div className="mt-3 grid grid-cols-2 gap-2 text-sm">
                                                <div className="text-gray-500">Format</div>
                                                <div className="text-right">{describeReportFormat(report)}</div>
                                                <div className="text-gray-500">Owner Payout</div>
                                                <div className="text-right font-mono">{formatMoney(report.totals_snapshot?.total_owner_payout)}</div>
                                                <div className="text-gray-500">Saved</div>
                                                <div className="text-right">{formatDateTime(report.input_date)}</div>
                                                <div className="text-gray-500">Emailed</div>
                                                <div className="text-right">{emailedLabel(report)}</div>
                                            </div>
                                            {report.status === 'superseded' && <p className="mt-2 text-xs text-gray-500">{supersededHint(report)}</p>}
                                            <div className="mt-3">{actionButtons(report)}</div>
                                            {rowError[report.id] && <p className="mt-1 text-xs text-red-500">{rowError[report.id]}</p>}
                                        </div>
                                    ))}
                                </div>

                                <div className="mt-4 flex items-center justify-between text-sm text-gray-600">
                                    <span>{offset + 1}–{pageEnd} of {total}</span>
                                    <div className="flex gap-2">
                                        <Button size="sm" variant="outlined" disabled={offset === 0 || loading} onClick={() => setOffset(Math.max(offset - PAGE_SIZE, 0))}>Previous</Button>
                                        <Button size="sm" variant="outlined" disabled={pageEnd >= total || loading} onClick={() => setOffset(offset + PAGE_SIZE)}>Next</Button>
                                    </div>
                                </div>
                            </>
                        )}
                    </CardBody>
                </Card>
            </div>

            {/* Email dialog */}
            <Dialog open={!!emailTarget} handler={closeEmail} size="md">
                <DialogHeader className="text-lg">Email report</DialogHeader>
                <DialogBody className="max-h-[70vh] space-y-4 overflow-y-auto">
                    {emailTarget && (
                        <p className="text-sm text-gray-600">
                            {emailTarget.owner_name || emailTarget.owner_code} · Week {emailTarget.week_key} · {describeReportFormat(emailTarget)}
                            {emailTarget.status === 'superseded' && emailTarget.superseded_reason === 'recalculated' && (
                                <span className="mt-2 block text-amber-700">
                                    This copy is outdated: the week's figures have changed since it was saved. Regenerate it first to send current numbers.
                                </span>
                            )}
                        </p>
                    )}
                    <div>
                        <label htmlFor="report-email-recipient" className="mb-1 block text-sm font-medium text-gray-700">Recipient email</label>
                        <input
                            id="report-email-recipient"
                            type="email"
                            value={emailForm.recipient}
                            onChange={(event) => { setEmailForm({ ...emailForm, recipient: event.target.value }); setEmailError(''); }}
                            placeholder="Leave blank to use the owner's email on file"
                            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#FF5733]"
                        />
                    </div>
                    <div>
                        <label htmlFor="report-email-subject" className="mb-1 block text-sm font-medium text-gray-700">Subject</label>
                        <input
                            id="report-email-subject"
                            value={emailForm.subject}
                            onChange={(event) => setEmailForm({ ...emailForm, subject: event.target.value })}
                            disabled={emailDraftLoading}
                            placeholder={emailDraftLoading ? 'Preparing subject...' : 'Leave blank to use the default subject'}
                            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#FF5733]"
                        />
                    </div>
                    <div>
                        <div className="mb-1 flex items-center justify-between gap-2">
                            <label htmlFor="report-email-message" className="block text-sm font-medium text-gray-700">Message</label>
                            {emailDraft && (emailForm.subject !== emailDraft.subject || emailForm.message !== emailDraft.message) && (
                                <button type="button" onClick={resetEmailDraft} className="text-xs font-semibold text-[#FF5733] hover:underline">
                                    Reset to suggested text
                                </button>
                            )}
                        </div>
                        <textarea
                            id="report-email-message"
                            rows={9}
                            value={emailForm.message}
                            onChange={(event) => setEmailForm({ ...emailForm, message: event.target.value })}
                            disabled={emailDraftLoading}
                            placeholder={emailDraftLoading ? 'Preparing message...' : 'Leave blank to use the default message'}
                            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#FF5733]"
                        />
                    </div>
                    {emailError && <p className="text-sm text-red-500">{emailError}</p>}
                    {emailSuccess && <p className="text-sm text-green-700">{emailSuccess}</p>}
                    {emailHistory.length > 0 && (
                        <div>
                            <div className="mb-1 text-sm font-medium text-gray-700">Previous sends</div>
                            <ul className="space-y-1 text-xs text-gray-600">
                                {emailHistory.map((entry) => (
                                    <li key={entry.id} className="flex justify-between gap-2">
                                        <span className="break-all">{entry.recipient_email}</span>
                                        <span className={entry.status === 'failed' ? 'text-red-500' : ''}>
                                            {entry.status === 'failed' ? 'Failed' : 'Sent'} {formatDateTime(entry.input_date)} · {entry.input_by}
                                        </span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </DialogBody>
                <DialogFooter className="gap-2">
                    <Button variant="text" onClick={closeEmail}>Close</Button>
                    <Button className="flex items-center gap-2 bg-[#FF5733]" onClick={sendEmail} disabled={emailSending || emailDraftLoading}>
                        <FontAwesomeIcon icon={emailSending ? faSpinner : faEnvelope} spin={emailSending} />
                        {emailSending ? 'Sending...' : 'Send email'}
                    </Button>
                </DialogFooter>
            </Dialog>

            {/* Share dialog */}
            <Dialog open={!!shareTarget} handler={() => setShareTarget(null)} size="md">
                <DialogHeader className="text-lg">Share link</DialogHeader>
                <DialogBody className="space-y-3">
                    {!shareLink && !shareError && (
                        <div className="flex items-center gap-2 text-gray-600"><FontAwesomeIcon icon={faSpinner} spin /> Creating link...</div>
                    )}
                    {shareLink && (
                        <>
                            <p className="text-sm text-gray-600">
                                Anyone with this link can open this one report until {formatDateTime(shareLink.expires_at)}. Creating a new link replaces the old one.
                            </p>
                            <label htmlFor="report-share-url" className="block text-sm font-medium text-gray-700">Link</label>
                            <div className="flex gap-2">
                                <input
                                    id="report-share-url"
                                    readOnly
                                    value={shareLink.url}
                                    onFocus={(event) => event.target.select()}
                                    className="w-full rounded-md border border-gray-300 bg-gray-50 px-3 py-2 text-xs"
                                />
                                <Button size="sm" className="flex items-center gap-1 bg-[#FF5733]" onClick={copyShareLink}>
                                    <FontAwesomeIcon icon={copied ? faCheck : faCopy} />
                                    {copied ? 'Copied' : 'Copy'}
                                </Button>
                            </div>
                        </>
                    )}
                    {shareError && <p className="text-sm text-red-500">{shareError}</p>}
                </DialogBody>
                <DialogFooter>
                    <Button variant="text" onClick={() => setShareTarget(null)}>Close</Button>
                </DialogFooter>
            </Dialog>

            <ConfirmModal
                isOpen={!!regenerateTarget}
                title="Regenerate this report?"
                description={regenerateTarget
                    ? `A new ${describeReportFormat(regenerateTarget)} for ${regenerateTarget.owner_code}, week ${regenerateTarget.week_key}, will be built from the figures saved now and stored in Google Drive. This copy stays in Drive and is marked replaced. Nothing is recalculated.`
                    : ''}
                confirmText="Regenerate"
                variant="primary"
                loading={regenerating}
                error={regenerateError}
                onCancel={() => setRegenerateTarget(null)}
                onConfirm={confirmRegenerate}
            />

        </div>
    );
};

export default RemittanceReports;
